// src/components/resources/tools/generators/EmailThread/EmailThreadGenerator.tsx

'use client'

import Link from 'next/link'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Trash2, Plus, Download } from 'lucide-react'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import { PostExportPrompt } from '../shared/PostExportPrompt'
import { usePostExportPrompt } from '../shared/usePostExportPrompt'
import { GeneratorExportBridge } from '../shared/GeneratorExportBridge'
import { trackGeneratorBridgeClick } from '../shared/trackGeneratorBridge'
import {
  generatorHasExportHint,
  generatorHasPostExportFunnel,
  resolveEmailPostExportVariants,
} from '~/lib/resources/generator-growth-config'
import { GmailPreview } from './GmailPreview'
import { OutlookPreview } from './OutlookPreview'
import { createMessageTemplate } from './email-thread-utils'
import { DEFAULT_CONFIG, DEFAULT_MESSAGES } from './types'
import type { EmailClient, EmailConfigChange, EmailMessage, EmailThreadConfig } from './types'

// Preserve this module's pre-decomposition public surface: these types were
// exported from EmailThreadGenerator before they moved to ./types, so re-export
// them here so any external `import type { EmailClient, ... } from
// './EmailThreadGenerator'` keeps resolving (no behavior change).
export type { EmailClient, EmailMessage, EmailThreadConfig } from './types'

/**
 * Props for the Email Thread Generator.
 *
 * `initialConfig` lets landing-page variants (Outlook variant, future
 * Gmail variant) boot the generator pre-configured for a specific email
 * client. Pass `{ client: 'outlook' }` to default-select Outlook
 * styling; callers only override fields they care about. The
 * `messages` array, when provided, replaces the default — the
 * messageCountRef counter is initialised from whichever length is
 * in effect, so subsequent addMessage() calls produce non-colliding
 * IDs.
 */
export interface EmailThreadGeneratorProps {
  initialConfig?: Partial<EmailThreadConfig>
  /**
   * Engagement-event attribution slug. Defaults to the umbrella
   * generator's slug so existing call sites keep their analytics
   * stream unchanged. The Outlook landing-page variant passes its
   * own slug so export / interact events resolve to the variant URL
   * in GA / experiment tooling instead of being folded into the
   * umbrella's counts.
   */
  trackingSlug?: string
}

/**
 * Main generator component for the email thread preview and controls.
 */
export function EmailThreadGenerator({ initialConfig, trackingSlug = 'email-thread-mockup-generator' }: EmailThreadGeneratorProps = {}): JSX.Element {
  const [config, setConfig] = useState<EmailThreadConfig>(() => ({ ...DEFAULT_CONFIG, ...initialConfig }))
  // Render the heavy email-thread preview from a `useDeferredValue` snapshot so
  // typing/toggling in the controls commits immediately (controls read live
  // `config`) while the preview tree re-renders at a lower priority. Export
  // waits for this snapshot to converge so the PNG/WebP matches the controls.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  const previewStaleRef = useStaleRef(isStale)
  // Shared export lifecycle: warms html2canvas on mount, a synchronous
  // re-entrancy guard, and the busy flag. `exportingFormat` tracks WHICH format
  // is capturing so only the clicked button shows the spinner while both disable.
  const { isExporting, runExport } = useExport()
  const [exportingFormat, setExportingFormat] = useState<'png' | 'webp' | null>(null)
  // Initial counter must reflect whatever `messages` actually ships
  // in the merged config — not blindly use DEFAULT_MESSAGES.length.
  // Otherwise a caller passing `initialConfig.messages` of a
  // different length would issue colliding `message-N` IDs on the
  // first addMessage() call.
  const messageCountRef = useRef<number>(
    (initialConfig?.messages?.length ?? DEFAULT_MESSAGES.length) + 1,
  )
  const previewRef = useRef<HTMLDivElement>(null)
  
  // Analytics: Create engagement timers once and keep them stable
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])
  const { open: postExportOpen, onOpenChange: onPostExportOpenChange, promptAfterExport } =
    usePostExportPrompt(trackingSlug)

  const postExportVariantLinks = useMemo(
    () => resolveEmailPostExportVariants(config.client, trackingSlug),
    [config.client, trackingSlug],
  )
  const showGrowthFunnel = generatorHasPostExportFunnel(trackingSlug)
  const showExportHint = generatorHasExportHint(trackingSlug)

  /**
   * Adds a new message to the thread.
   */
  const addMessage = useCallback((): void => {
    const currentCount = messageCountRef.current
    
    setConfig(prev => ({
      ...prev,
      messages: [...prev.messages, createMessageTemplate(currentCount)],
    }))
    
    messageCountRef.current += 1
    
    // Track message addition - user clicked add message button to create new email
    sendResourceEvent('interact', {
      resourceType: 'tool',
      resourceSlug: trackingSlug,
      engagementMs: getDeltaMs(),
      params: {
        tool_type: 'generator',
        interaction_type: 'add_message',
        message_count: currentCount + 1
      }
    })
  }, [getDeltaMs, trackingSlug])

  /**
   * Updates a message in the thread by id.
   */
  const updateMessage = useCallback((id: string, updates: Partial<EmailMessage>): void => {
    setConfig(prev => ({
      ...prev,
      messages: prev.messages.map(message => (message.id === id ? { ...message, ...updates } : message)),
    }))
  }, [])

  /**
   * Toggles a single message's star, used by the Gmail preview row.
   * Stable (empty deps, functional update) so `GmailMessageRow`'s
   * `React.memo` is not defeated and the mutation always reads live state.
   */
  const toggleMessageStar = useCallback((id: string): void => {
    setConfig(prev => ({
      ...prev,
      messages: prev.messages.map(message => (message.id === id ? { ...message, starred: !message.starred } : message)),
    }))
  }, [])

  /**
   * Removes a message from the thread by id.
   */
  const removeMessage = useCallback((id: string): void => {
    setConfig(prev => ({
      ...prev,
      messages: prev.messages.filter(message => message.id !== id),
    }))
  }, [])

  /**
   * Sets whether avatars are shown in the preview.
   */
  const setShowAvatars = useCallback((show: boolean): void => {
    setConfig(prev => ({ ...prev, showAvatars: show }))
  }, [])

  /**
   * Sets whether to highlight the current user's reply.
   */
  const setHighlightReply = useCallback((highlight: boolean): void => {
    setConfig(prev => ({ ...prev, highlightReply: highlight }))
  }, [])

  /**
   * Handles config changes from preview components.
   */
  const handleConfigChange = useCallback<EmailConfigChange>((updates): void => {
    setConfig(prev => ({ ...prev, ...(typeof updates === 'function' ? updates(prev) : updates) }))
  }, [])

  /**
   * Downloads the thread preview using html2canvas. Supports PNG (lossless,
   * print-ready) and WebP (smaller files for web) so the rendered surface
   * matches the two download buttons advertised in `emailThreadGenerator.ts`
   * (featureList + FAQ + output.variants).
   */
  const downloadImage = useCallback((format: 'png' | 'webp'): Promise<void> => runExport(async beginCapture => {
    // Settle FIRST (let any pending render/paint complete), THEN gate on
    // stability — so the check is the last async step before the synchronous node
    // read + capture. Gating before the 200ms settle would leave a window in which
    // a stray control edit advances `config`, the deferred preview goes stale
    // again, and the capture reads a mid-update DOM (the stale frame the gate
    // exists to prevent).
    await new Promise(resolve => setTimeout(resolve, 200))

    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge. Switching client (Gmail↔Outlook) unmounts the old
    // `#email-thread-preview` node and mounts a new one on the deferred commit, so
    // we resolve the node AFTER the wait — resolving it before would capture a
    // detached, stale subtree. Fail loudly if it can't settle.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating', {
        description: 'Pause a moment, then download again.'
      })
      return
    }

    const element = document.getElementById('email-thread-preview') ?? document.getElementById('email-thread-inner-preview')
    if (!element) {
      toast.error('Preview not available', {
        description: 'Please ensure the preview is visible before downloading.'
      })
      return
    }

    // Capture is starting — surface the busy state on the clicked format's
    // button so the multi-second html2canvas block isn't silent.
    setExportingFormat(format)
    beginCapture()
    try {
      await captureAndDownload({
        element,
        format,
        filename: `email-thread-mockup-${new Date().toISOString().split('T')[0]}`,
        trackingSlug,
        engagementMs: getTotalMs(),
        extraParams: { message_count: config.messages.length },
        onSuccess: () => {
          toast.success('Download started', {
            description: 'Your email thread image is being downloaded.',
          })
          if (showGrowthFunnel) promptAfterExport()
        },
        onError: () =>
          toast.error(`Failed to generate ${format.toUpperCase()}`, {
            description: 'An error occurred while creating the image. Please try again.'
          }),
      })
    } finally {
      setExportingFormat(null)
    }
  }), [runExport, config.messages.length, getTotalMs, trackingSlug, previewStaleRef, promptAfterExport, showGrowthFunnel])

  // Memoized preview
  const preview = useMemo(() => {
    return deferredConfig.client === 'gmail'
      ? <GmailPreview config={deferredConfig} onConfigChange={handleConfigChange} onToggleMessageStar={toggleMessageStar} />
      : <OutlookPreview config={deferredConfig} onConfigChange={handleConfigChange} />
  }, [deferredConfig, handleConfigChange, toggleMessageStar])

  return (
    <div className="flex w-full flex-col gap-8 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:w-[350px]">
        <section aria-labelledby="email-client" className="space-y-3">
          <h2 id="email-client" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Client styling
          </h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {(['gmail', 'outlook'] as EmailClient[]).map(client => (
              <button
                key={client}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, client }))}
                className={clsx(
                  'rounded-xl border px-3 py-2 font-medium capitalize transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  config.client === client
                    ? 'border-transparent bg-slate-900 text-white shadow-lg dark:bg-blue-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                )}
                aria-pressed={config.client === client}
              >
                {client}
              </button>
            ))}
          </div>
          {trackingSlug === 'email-thread-mockup-generator' && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Platform-specific entry points:{' '}
              <Link
                href="/resources/tools/generators/gmail-email-mockup-generator"
                onClick={() =>
                  trackGeneratorBridgeClick({
                    resourceSlug: trackingSlug,
                    placement: 'in_tool_variant',
                    targetType: 'variant_page',
                    destinationPath: '/resources/tools/generators/gmail-email-mockup-generator',
                    engagementMs: getTotalMs(),
                  })
                }
                className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                Gmail mockup
              </Link>
              {' · '}
              <Link
                href="/resources/tools/generators/outlook-email-mockup-generator"
                onClick={() =>
                  trackGeneratorBridgeClick({
                    resourceSlug: trackingSlug,
                    placement: 'in_tool_variant',
                    targetType: 'variant_page',
                    destinationPath: '/resources/tools/generators/outlook-email-mockup-generator',
                    engagementMs: getTotalMs(),
                  })
                }
                className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                Outlook mockup
              </Link>
            </p>
          )}
        </section>

        <section aria-labelledby="thread-meta" className="space-y-3">
          <h2 id="thread-meta" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Thread metadata
          </h2>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-200">Subject line</span>
            <input
              type="text"
              value={config.subject}
              onChange={event => setConfig(prev => ({ ...prev, subject: event.target.value }))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              aria-label="Subject line"
            />
          </label>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.showAvatars ?? true}
                onChange={event => setShowAvatars(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-blue-500 dark:focus:ring-blue-500"
                aria-checked={config.showAvatars ?? true}
              />
              Show avatars
            </label>
            <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.highlightReply ?? true}
                onChange={event => setHighlightReply(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-blue-500 dark:focus:ring-blue-500"
                aria-checked={config.highlightReply ?? true}
              />
              Mark as your reply
            </label>
          </div>
        </section>

        <section aria-labelledby="messages" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 id="messages" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Messages ({config.messages.length})
            </h2>
            <button
              type="button"
              onClick={addMessage}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-blue-500 dark:hover:bg-blue-400"
              aria-label="Add message"
            >
              <Plus className="h-3.5 w-3.5" />
              Add message
            </button>
          </div>

          <div className="space-y-4">
            {config.messages.map((message, index) => (
              <div key={message.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <span>Message {index + 1}</span>
                  {config.messages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMessage(message.id)}
                      className="text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
                      aria-label="Remove message"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <label className="mt-3 flex flex-col gap-1 text-xs">
                  <span className="font-medium text-slate-600 dark:text-slate-300">Sender name</span>
                  <input
                    type="text"
                    value={message.sender}
                    onChange={event => updateMessage(message.id, { sender: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Sender name"
                  />
                </label>
                <label className="mt-3 flex flex-col gap-1 text-xs">
                  <span className="font-medium text-slate-600 dark:text-slate-300">Email</span>
                  <input
                    type="email"
                    value={message.email}
                    onChange={event => updateMessage(message.id, { email: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Sender email"
                  />
                </label>
                <label className="mt-3 flex flex-col gap-1 text-xs">
                  <span className="font-medium text-slate-600 dark:text-slate-300">Timestamp</span>
                  <input
                    type="text"
                    value={message.timestamp}
                    onChange={event => updateMessage(message.id, { timestamp: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Timestamp"
                  />
                </label>
                <label className="mt-3 flex flex-col gap-1 text-xs">
                  <span className="font-medium text-slate-600 dark:text-slate-300">Message content</span>
                  <textarea
                    value={message.content}
                    onChange={event => updateMessage(message.id, { content: event.target.value })}
                    className="min-h-[100px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    aria-label="Message content"
                  />
                </label>
                <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={message.isCurrentUser ?? false}
                    onChange={event => updateMessage(message.id, { isCurrentUser: event.target.checked })}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-blue-500 dark:focus:ring-blue-500"
                    aria-checked={message.isCurrentUser ?? false}
                  />
                  Mark as your reply
                </label>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="export" className="space-y-3">
          <h3 id="export" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export guidance
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Export the thread for stakeholder updates, investor memos, or documentation. PNG for print-ready decks, WebP for smaller web files. Capture at full width for retina decks.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <ExportButton
              isExporting={exportingFormat === 'png'}
              disabled={isExporting}
              onExport={() => void downloadImage('png')}
              icon={<Download className="h-4 w-4" />}
              aria-label="Download PNG"
              className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-500 dark:hover:bg-blue-400"
            />
            <ExportButton
              isExporting={exportingFormat === 'webp'}
              disabled={isExporting}
              onExport={() => void downloadImage('webp')}
              icon={<Download className="h-4 w-4" />}
              label="Download WebP"
              aria-label="Download WebP"
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>
          {showExportHint && (
            <GeneratorExportBridge resourceSlug={trackingSlug} getEngagementMs={getTotalMs} />
          )}
        </section>
      </aside>

      <div
        className="flex flex-1 items-center justify-center rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-black p-2 sm:p-6"
        ref={previewRef}
      >
        {preview}
      </div>

      {showGrowthFunnel && (
        <PostExportPrompt
          open={postExportOpen}
          onOpenChange={onPostExportOpenChange}
          resourceSlug={trackingSlug}
          getEngagementMs={getTotalMs}
          variantLinks={postExportVariantLinks}
        />
      )}
    </div>
  )
}
